'use client';

import { useMemo } from 'react';
import BlogCard from '@/app/(header)/blog/_components/BlogCard';
import { blogPosts } from '@/app/_data/posts';
import { getLastModifiedDate, isPublished, type PostMeta } from '@/app/_data/posts.types';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';

function formatLastModified(meta: PostMeta): string {
  return getLastModifiedDate(meta).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const BlogSection = () => {
  /** 발행 글만 최종 수정일(getLastModifiedDate) 내림차순으로 6개 */
  const blogPostsData = useMemo(
    () =>
      Object.entries(blogPosts)
        .filter(([, post]) => isPublished(post.meta))
        .sort((a, b) => {
          const timeA = getLastModifiedDate(a[1].meta).getTime();
          const timeB = getLastModifiedDate(b[1].meta).getTime();
          if (timeB !== timeA) {
            return timeB - timeA;
          }

          return a[0].localeCompare(b[0]);
        })
        .slice(0, 6)
        .map(([key, post]) => ({ key, ...post })),
    [],
  );

  return (
    <section id='blog' className='py-[120px] px-6 max-w-[1100px] mx-auto'>
      <div className='text-xs font-bold uppercase tracking-[0.12em] text-primary mb-3.5 text-center'>BLOG</div>
      <h2
        className='font-display font-black leading-[1.05] tracking-[-0.04em] text-center mb-12'
        style={{ fontSize: 'clamp(32px, 4.5vw, 52px)' }}
      >
        Insights from <span className='gradient-text'>Tindlo Tech</span>
      </h2>

      <ul className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {blogPostsData.map(post => (
          <li key={post.key} className='list-none flex'>
            <BlogCard
              key={post.key}
              keyValue={post.key}
              title={post.title}
              description={post.meta?.description || ''}
              category={post.category}
              categorySlug={post.categorySlug}
              date={formatLastModified(post.meta as PostMeta)}
              author={post.meta?.author || ''}
            />
          </li>
        ))}
      </ul>

      <div className='flex justify-center mt-10'>
        <Link
          href='/blog'
          className='inline-flex items-center gap-2 px-8 py-3 rounded-full border border-border text-sm font-semibold text-foreground hover:border-[#7c3aed] hover:text-[#7c3aed] transition-all'
        >
          <span>View more</span>
          <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
};

export default BlogSection;
